import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { type FC, useState } from 'react'
import { useParams } from 'react-router'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Button, CollapsibleContainer } from 'slab'
import { capitalize } from 'utils'
import {
  getGetBrokersTasksByIdTaskIdResultsQueryKey,
  getGetBrokersTasksTaskIdQueryKey,
  useGetBrokersTasksTaskId,
  useGetBrokersTaskWorkflowsId,
} from 'utils/web_api'
import { type BrokersTask, type BrokersWorkflowSummary } from 'utils/web_api.schemas'

import TaskEnrollmentDetails from './TaskEnrollmentDetails'
import WorkflowProgressBar from '../WorkflowProgressBar'
import AuditHistoryDrawer from '../workflows/AuditHistoryDrawer'
import TaskActionControls, { useTaskActions } from '../workflows/TaskActionControls'
import {
  ExecutionHistorySection,
  PlaywrightExecutionSection,
  RunbookInstructionsSection,
  TaskActionsSection,
  TaskMetadataSection,
  TaskNotesSection,
} from '../workflows/TaskAdminSections'
import TaskCardHeader from '../workflows/TaskCardHeader'
import TaskDetailsSection from '../workflows/TaskDetailsSection'
import { useTaskAdminActions } from '../workflows/useTaskAdminActions'
import { useTaskMetadata } from '../workflows/useTaskMetadata'
import WorkflowActionControls from '../workflows/WorkflowActionControls'
import WorkflowDAG from '../workflows/WorkflowDAG'
import { WorkflowStatusBadge } from '../workflows/WorkflowVisualIndicators'

type TaskDetailsPageProps = {
  isUserThatchWorkflowAdmin: boolean
  stripePublishableKey: string
}

// Animated Chevron Component for audit history toggles
const AnimatedChevron: FC<{ isOpen: boolean }> = ({ isOpen }) => {
  return (
    <ChevronDown
      size={14}
      className={`transition-transform duration-200 ease-in-out ${
        isOpen ? 'rotate-0' : '-rotate-90'
      }`}
    />
  )
}

const TaskDetailsPage: FC<TaskDetailsPageProps> = ({
  isUserThatchWorkflowAdmin,
  stripePublishableKey,
}) => {
  const { id: taskId } = useParams<{ id: string }>()
  const [taskAuditHistoryOpen, setTaskAuditHistoryOpen] = useState(false)
  const queryClient = useQueryClient()
  const taskIdOrFallback = taskId || ''

  const {
    data: task,
    isLoading,
    error,
  } = useGetBrokersTasksTaskId(
    taskIdOrFallback,
    {
      query: {
        refetchInterval: (query) => {
          const isTaskProcessing = query.state.data?.status === 'processing'
          return isTaskProcessing ? 3000 : false
        },
      },
    }
  )

  const {
    data: workflow,
    isLoading: workflowLoading,
  } = useGetBrokersTaskWorkflowsId(
    task?.workflow_id || '',
    {},
    { query: { enabled: Boolean(task?.workflow_id) } }
  )

  const adminActions = useTaskAdminActions(
    taskId,
    task?.workflow_id || null,
    isUserThatchWorkflowAdmin
  )
  const metadata = useTaskMetadata(task)

  const { handleTaskAction, isLoading: taskActionLoading } = useTaskActions(
    task?.workflow_id || '',
    {
      enrollmentId: task?.related_id,
      memberId: task?.member_id,
      onSuccess: (changedTaskId) => {
        // Refresh queries when task is updated
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTasksTaskIdQueryKey(changedTaskId),
        })
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTasksByIdTaskIdResultsQueryKey(changedTaskId),
        })
      },
    }
  )

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 pb-6'>
        <div className='p-8 text-center text-red-500'>
          Error: {(error as GrapeAPIErrorType).message}
        </div>
      </div>
    )
  }

  if (!task) {
    return (
      <div className='container mx-auto px-4 pb-6'>
        <div className='p-8 text-center text-gray-500'>Task not found</div>
      </div>
    )
  }

  const formatTaskType = (type: string) => {
    return type
      .replace(/Task$/, '')
      .replace(/([A-Z])/g, ' $1')
      .trim()
  }

  const formatWorkflowType = (type: string) => {
    return type.split('_').map(capitalize).join(' ')
  }

  const tasks = workflow?.tasks_summary || []
  const hasV2Tasks = tasks.some(t => t.task_type?.startsWith('Tasks::V2::'))
  const completedCount = workflow?.completed_task_count ?? 0
  const totalCount = workflow?.task_count ?? 0
  const failedCount = workflow?.failed_task_count ?? 0

  return (
    <BrokersMemberContext.Provider
      value={{
        stripePublishableKey,
        isUserThatchAdmin: true,
        isUserThatchWorkflowAdmin,
        memberId: task.member_id || '',
        planYear: 0, // BrokersTaskDetail doesn't have plan_year
      }}
    >
      <div className='container mx-auto px-4 pb-6'>
        <div className='space-y-6'>
          {/* Workflow Section */}
          {workflow && !workflowLoading && (
            <div className='rounded-lg border shadow-sm bg-white'>
              <div className='px-6 py-4 border-b'>
                <div className='flex items-start justify-between'>
                  <div className='flex items-center gap-3'>
                    <h2 className='text-lg font-semibold text-gray-900'>
                      {formatWorkflowType(workflow.workflow_type)}
                    </h2>
                    <WorkflowStatusBadge status={workflow.status} />
                    <Button
                      type='button'
                      variant='tertiary-deemphasized'
                      size='small'
                      onClick={() => window.open(`/brokers/workflows/${workflow.id}`, '_blank')}
                    >
                      <div className='flex items-center gap-1'>
                        View Workflow
                        <ExternalLink size={14} />
                      </div>
                    </Button>
                  </div>
                  <WorkflowActionControls
                    workflow={workflow as unknown as BrokersWorkflowSummary}
                  />
                </div>
              </div>

              <div className='bg-white p-6 space-y-4'>
                <div className='space-y-3'>
                  <div className='flex justify-between text-sm'>
                    <span>Task Progress</span>
                    <span>
                      {completedCount}/{totalCount} completed
                      {failedCount > 0 && `, ${failedCount} failed`}
                    </span>
                  </div>
                  <WorkflowProgressBar tasks={tasks} />
                </div>

                {hasV2Tasks && (
                  <CollapsibleContainer
                    heading='Workflow Dependency Graph'
                    defaultOpen={false}
                  >
                    <WorkflowDAG tasks={tasks} />
                  </CollapsibleContainer>
                )}
              </div>
            </div>
          )}

          {/* Task Overview */}
          <div className='rounded-lg border shadow-sm bg-white'>
            {/* Header with task status information */}
            <div className='px-6 py-4 border-b'>
              <div className='flex items-start justify-between'>
                <TaskCardHeader task={task as BrokersTask} showSequenceOrder />
              </div>
            </div>

            {/* Content */}
            <div className='bg-white p-6'>
              <div className='space-y-4'>
                <TaskDetailsSection task={task as BrokersTask} showBlockedInfo />

                {/* Task Actions */}
                <div className='flex items-center justify-between pt-2 border-t border-gray-200'>
                  <div className='flex items-center gap-2'>
                    <Button
                      type='button'
                      variant='tertiary-deemphasized'
                      size='small'
                      onClick={() => setTaskAuditHistoryOpen(!taskAuditHistoryOpen)}
                    >
                      <div className='flex items-center gap-2'>
                        Task Audit History
                        <AnimatedChevron isOpen={taskAuditHistoryOpen} />
                      </div>
                    </Button>
                  </div>
                  <TaskActionControls
                    task={task as BrokersTask}
                    onTaskAction={handleTaskAction}
                    isLoading={taskActionLoading}
                    workflowId={task.workflow_id || ''}
                    enrollmentId={task.related_id || ''}
                  />
                </div>
              </div>

              {/* Audit History Drawer */}
              <AuditHistoryDrawer
                params={{
                  auditable_type: 'Tasks::Task',
                  auditable_id: task.id,
                }}
                isOpen={taskAuditHistoryOpen}
                title={`Task Audit History - ${task.display_name || formatTaskType(task.task_type || '')}`}
                containerClasses='px-4'
              />
            </div>
          </div>

          {/* Enrollment Details */}
          <TaskEnrollmentDetails task={task} />

          {/* Admin-only sections */}
          {isUserThatchWorkflowAdmin && task && taskId && (
            <>
              <PlaywrightExecutionSection
                task={task}
                taskId={taskId}
                adminActions={adminActions}
              />

              <TaskActionsSection
                task={task}
                adminActions={adminActions}
                headingLevel={3}
              />

              <RunbookInstructionsSection metadata={metadata} headingLevel={3} />

              <TaskMetadataSection task={task} headingLevel={3} />

              <ExecutionHistorySection
                taskId={taskId}
                adminActions={adminActions}
                headingLevel={3}
              />

              <TaskNotesSection task={task} headingLevel={3} />
            </>
          )}
        </div>
      </div>
    </BrokersMemberContext.Provider>
  )
}

export default TaskDetailsPage
